import { useState } from 'react'
import {
  Stack, Title, Text, Table, Badge, Group, Select, TextInput, Button, Alert,
} from '@mantine/core'
import { notifications } from '@mantine/notifications'
import { IconDownload, IconAlertCircle, IconLock } from '@tabler/icons-react'
import { useQuery } from '@tanstack/react-query'
import { chamadosApi, type ChangeReportFilters, type ChangeReportRow } from '@/api/chamados'
import { useCurrentTenant, useCan } from '@/store/auth'
import { DataCard } from '@/components/ui'
import { StatusBadge, opcoesDeStatus } from '@/components/chamados'

const ENV_OPTIONS = [
  { value: '', label: 'Todos os ambientes' },
  { value: 'dev', label: 'Dev' },
  { value: 'qa', label: 'QA' },
  { value: 'hml', label: 'HML' },
  { value: 'prd', label: 'Produção' },
]

const STATUS_OPTIONS = [
  { value: '', label: 'Todos os status' },
  ...opcoesDeStatus([
    'gmud_pending_cab', 'gmud_approved', 'gmud_scheduled', 'gmud_executing',
    'gmud_completed', 'gmud_failed', 'gmud_closed',
  ]),
]

function SimNao({ value, goodColor = 'green' }: { value: boolean; goodColor?: string }) {
  return (
    <Badge size="xs" variant="light" color={value ? goodColor : 'red'}>
      {value ? 'Sim' : 'Não'}
    </Badge>
  )
}

export function RelatorioMudancas() {
  const tenantId = useCurrentTenant()?.id
  const can = useCan()
  // O GET de change-reports agora exige gmud_read no backend. A pagina inteira
  // e sobre GMUD — sem a permissao nao ha bloco parcial a mostrar, e desligar
  // so a query sem escondar o conteudo deixaria a tabela vazia sem explicar
  // por que (alem do 403 inutil no console).
  const canGmudRead = can('chamados', 'gmud_read')
  const [environment, setEnvironment] = useState('')
  const [status, setStatus] = useState('')
  const [dateFrom, setDateFrom] = useState('')
  const [dateTo, setDateTo] = useState('')
  const [downloading, setDownloading] = useState(false)

  const filters: ChangeReportFilters = {
    ...(environment ? { environment } : {}),
    ...(status ? { status } : {}),
    ...(dateFrom ? { date_from: dateFrom } : {}),
    ...(dateTo ? { date_to: dateTo } : {}),
  }

  const { data: rows = [], isLoading, error } = useQuery<ChangeReportRow[]>({
    queryKey: ['change-report', tenantId, filters],
    queryFn: async () => (await chamadosApi.getChangeReport(Number(tenantId), filters)).data,
    enabled: !!tenantId && canGmudRead,
  })

  if (!canGmudRead) {
    return (
      <Alert icon={<IconLock size={16} />} color="yellow" variant="light">
        Você não tem permissão para visualizar o relatório de mudanças.
      </Alert>
    )
  }

  const handleExport = async () => {
    if (!tenantId) return
    setDownloading(true)
    try {
      const res = await chamadosApi.downloadChangeReportCsv(Number(tenantId), filters)
      const blob = new Blob([res.data as BlobPart], { type: 'text/csv;charset=utf-8;' })
      const url = URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = 'relatorio-mudancas.csv'
      document.body.appendChild(a)
      a.click()
      a.remove()
      URL.revokeObjectURL(url)
    } catch {
      notifications.show({ color: 'red', message: 'Não foi possível exportar o CSV.' })
    } finally {
      setDownloading(false)
    }
  }

  return (
    <Stack gap="md">
      <div>
        <Title order={3}>Relatório de Mudanças</Title>
        <Text size="sm" c="dimmed">
          Evidência PCI das mudanças: segregação executor≠validador, sign-off, rollback e evidência de teste.
        </Text>
      </div>

      {error && (
        <Alert icon={<IconAlertCircle size={16} />} color="red" variant="light">
          Não foi possível carregar o relatório. Tente novamente em instantes.
        </Alert>
      )}

      <Group align="flex-end" wrap="wrap">
        <Select
          label="Ambiente"
          data={ENV_OPTIONS}
          value={environment}
          onChange={(v) => setEnvironment(v ?? '')}
          w={180}
        />
        <Select
          label="Status"
          data={STATUS_OPTIONS}
          value={status}
          onChange={(v) => setStatus(v ?? '')}
          w={200}
        />
        <TextInput
          label="De (ISO)"
          placeholder="2026-01-01T00:00:00Z"
          value={dateFrom}
          onChange={(e) => setDateFrom(e.currentTarget.value)}
          w={210}
        />
        <TextInput
          label="Até (ISO)"
          placeholder="2026-12-31T23:59:59Z"
          value={dateTo}
          onChange={(e) => setDateTo(e.currentTarget.value)}
          w={210}
        />
        <Button
          leftSection={<IconDownload size={14} />}
          onClick={handleExport}
          loading={downloading}
          disabled={!tenantId || rows.length === 0}
        >
          Exportar CSV
        </Button>
      </Group>

      <DataCard flush
        title="Mudanças"
        accent="primary"
        badge={{ value: rows.length, color: 'blue' }}
        loading={isLoading}
      >
        <Table.ScrollContainer minWidth={900}>
          <Table striped highlightOnHover>
            <Table.Thead>
              <Table.Tr>
                <Table.Th>Protocolo</Table.Th>
                <Table.Th>Tipo</Table.Th>
                <Table.Th>Categoria</Table.Th>
                <Table.Th>Ambiente</Table.Th>
                <Table.Th>Status</Table.Th>
                <Table.Th>Executor≠Validador</Table.Th>
                <Table.Th>Sign-off</Table.Th>
                <Table.Th>Rollback</Table.Th>
                <Table.Th>Evidência</Table.Th>
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {rows.map((r) => (
                <Table.Tr key={r.protocol_number}>
                  {/* monospace + nowrap: o protocolo passou a ter 24-27 chars
                      (spec 2026-07-28) e o navegador quebra nos hifens, partindo
                      o identificador em duas linhas. A tabela ja rola no
                      ScrollContainer, entao nao quebrar nao aperta ninguem. */}
                  <Table.Td fw={500} ff="monospace" style={{ whiteSpace: 'nowrap' }}>
                    {r.protocol_number}
                  </Table.Td>
                  <Table.Td><Badge size="xs" variant="outline">{r.request_type}</Badge></Table.Td>
                  <Table.Td>{r.category ?? '—'}</Table.Td>
                  <Table.Td>{r.current_environment ?? '—'}</Table.Td>
                  <Table.Td><StatusBadge status={r.status} size="xs" /></Table.Td>
                  <Table.Td><SimNao value={r.segregation_ok} /></Table.Td>
                  <Table.Td><SimNao value={!!r.signed_off} /></Table.Td>
                  <Table.Td><SimNao value={r.has_rollback} /></Table.Td>
                  <Table.Td><SimNao value={r.has_evidence} /></Table.Td>
                </Table.Tr>
              ))}
              {rows.length === 0 && !isLoading && (
                <Table.Tr>
                  <Table.Td colSpan={9}>
                    <Text size="sm" c="dimmed" ta="center" py="md">Nenhuma mudança encontrada para os filtros.</Text>
                  </Table.Td>
                </Table.Tr>
              )}
            </Table.Tbody>
          </Table>
        </Table.ScrollContainer>
      </DataCard>
    </Stack>
  )
}
